import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { projects as projectsApi, tasks as tasksApi } from '../../services/api.js';
import { texteValidite } from '../../lib/validiteTaches.js';

export default function AdminTasks() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();

  const canCreate = can('tasks.create.all');
  const canUpdate = can('tasks.update.all');
  const canArchive = can('tasks.delete.all');

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [search, setSearch] = useState('');
  const [filterBilling, setFilterBilling] = useState('all');
  const [filterActive, setFilterActive] = useState('all');

  const [editingTask, setEditingTask] = useState(null);
  const [creating, setCreating] = useState(false);
  const [addingExisting, setAddingExisting] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  const reload = async () => {
    const t = await tasksApi.list(projectId);
    setTasks(t);
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([projectsApi.get(projectId), tasksApi.list(projectId)])
      .then(([p, t]) => {
        if (!active) return;
        setProject(p);
        setTasks(t);
        setPageError(null);
      })
      .catch(err => active && setPageError(err.message || 'Erreur'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [projectId]);

  const filtered = useMemo(() => {
    return tasks.filter(t => {
      if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterBilling === 'regie' && t.billingMethod !== 'regie') return false;
      if (filterBilling === 'forfait' && t.billingMethod !== 'forfait') return false;
      if (filterBilling === 'non-billable' && t.billable !== false) return false;
      if (filterActive === 'active' && !t.isActive) return false;
      if (filterActive === 'archived' && t.isActive) return false;
      return true;
    });
  }, [tasks, search, filterBilling, filterActive]);

  const handleSave = async (formData) => {
    const data = {
      name: formData.name.trim(),
      billable: formData.billable,
      // « facturable, méthode non précisée » (catalogue du 01/11/2026) : la méthode reste vide
      billingMethod: formData.billable ? (formData.billingMethod || null) : null,
      tarifType: (formData.billable && formData.billingMethod === 'regie') ? formData.tarifType : null,
      descRequired: formData.descRequired,
      validFrom: formData.validFrom || null,
      validTo: formData.validTo || null,
    };
    if (editingTask) {
      await tasksApi.update(projectId, editingTask.id, {
        ...data,
        replacedById: formData.validTo ? (formData.replacedById || null) : null,
        expectedUpdatedAt: editingTask.updatedAt,
      });
    } else {
      await tasksApi.create(projectId, data);
    }
    setEditingTask(null);
    setCreating(false);
    await reload();
  };

  const handleToggleActive = async (task) => {
    const ok = window.confirm(task.isActive
      ? `Voulez-vous archiver la tache "${task.name}" ?`
      : `Voulez-vous reactiver la tache "${task.name}" ?`);
    if (!ok) return;
    try {
      await tasksApi.toggleActive(projectId, task.id, task.updatedAt);
      await reload();
    } catch (err) {
      alert('Erreur: ' + (err.message || 'inattendue'));
    }
  };

  if (loading) return <div className="page-content"><p>Chargement...</p></div>;
  if (pageError) return <div className="page-content"><p style={{color:'red'}}>{pageError}</p></div>;
  if (!project) return <div className="page-content"><p>Projet introuvable</p></div>;

  return (
    <div className="page-content">
      <div style={{marginBottom:'1.5rem'}}>
        <button className="btn-secondary" onClick={() => navigate('/admin/projects')}>
          &larr; Retour aux projets
        </button>
      </div>

      <div className="page-header">
        <h1 style={{margin:0}}>
          Taches de "<span style={{color:'#6366f1'}}>{project.name}</span>"
          <span className="count-badge" style={{marginLeft:'0.75rem'}}>{filtered.length}</span>
        </h1>
        {canCreate && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-secondary" onClick={() => setAddingExisting(true)}>
              + Tache existante
            </button>
            <button className="btn-primary" onClick={() => setCreating(true)}>
              + Nouvelle tache
            </button>
          </div>
        )}
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select value={filterBilling} onChange={(e) => setFilterBilling(e.target.value)}>
          <option value="all">Toutes facturations</option>
          <option value="regie">Regie</option>
          <option value="forfait">Forfait</option>
          <option value="non-billable">Non facturable</option>
        </select>
        <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
          <option value="all">Tous les statuts</option>
          <option value="active">Actives</option>
          <option value="archived">Archivees</option>
        </select>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>TACHE</th>
            <th>FACTURATION</th>
            <th>TARIF</th>
            <th>DESC. REQUISE</th>
            <th>VALIDITÉ</th>
            <th>STATUT</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan="7" className="empty-state">Aucune tache</td></tr>
          ) : filtered.map(t => (
            <tr key={t.id} className={!t.isActive ? 'row-archived' : ''}>
              <td><strong>{t.name}</strong></td>
              <td>
                {t.billable === false ? <span className="badge-non-billable">Non facturable</span>
                  : t.billingMethod === 'regie' ? <span className="badge-regie">Regie</span>
                  : t.billingMethod === 'forfait' ? <span className="badge-forfait">Forfait</span>
                  : <span style={{color:'#999'}}>-</span>}
              </td>
              <td>
                {t.tarifType === 'operateur' ? 'Operateur'
                  : t.tarifType === 'special' ? 'Special'
                  : <span style={{color:'#999'}}>-</span>}
              </td>
              <td>{t.descRequired ? <span style={{color:'#22c55e',fontWeight:'bold'}}>OUI</span> : <span style={{color:'#999'}}>non</span>}</td>
              <td style={{fontSize:'0.85em'}}>
                {texteValidite(t) || <span style={{color:'#999'}}>toujours</span>}
                {t.replacedById && (
                  <div style={{color:'#64748b'}}>
                    &rarr; {tasks.find(x => x.id === t.replacedById)?.name || 'autre tâche'}
                  </div>
                )}
              </td>
              <td>
                {t.isActive
                  ? <span style={{color:'#22c55e'}}>&#9679; Actif</span>
                  : <span style={{color:'#999'}}>&#9675; Archive</span>}
              </td>
              <td className="actions">
                {canUpdate && <button className="btn-icon" onClick={() => setEditingTask(t)} title="Modifier">Edit</button>}
                {canArchive && (
                  <button className="btn-icon" onClick={() => handleToggleActive(t)} title={t.isActive ? 'Archiver' : 'Reactiver'}>
                    {t.isActive ? 'X' : 'OK'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {(creating || editingTask) && (
        <TaskModal
          task={editingTask}
          autresTaches={tasks.filter(x => x.id !== editingTask?.id)}
          onSave={handleSave}
          onClose={() => { setCreating(false); setEditingTask(null); }}
          onConflict={() => {
            setConflictData(true);
            setEditingTask(null);
            setCreating(false);
          }}
        />
      )}

      {addingExisting && (
        <ExistingTasksModal
          projectId={projectId}
          existingTaskNames={new Set(tasks.map(t => t.name))}
          onClose={() => setAddingExisting(false)}
          onSaved={async () => { setAddingExisting(false); await reload(); }}
        />
      )}

      {conflictData && (
        <div className="modal-overlay" onClick={() => setConflictData(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Conflit de mise a jour</h2>
            <p>La tache a ete modifiee entre temps. Rechargez les donnees pour continuer.</p>
            <div className="modal-actions">
              <button className="btn-primary" onClick={async () => {
                await reload();
                setConflictData(null);
              }}>Recharger</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TaskModal({ task, autresTaches = [], onSave, onClose, onConflict }) {
  // Une tâche existante « facturable sans méthode » (372 au 22/10 + le catalogue du 01/11/2026) garde cet état
  // tant qu'on ne choisit pas autre chose : modifier son nom ou sa validité ne change pas sa facturation.
  const sansMethodeAuDepart = !!task && task.billable !== false && !task.billingMethod;
  const [formData, setFormData] = useState({
    name: task?.name || '',
    billable: task?.billable !== undefined ? task.billable : true,
    billingMethod: task ? (task.billingMethod || '') : 'regie',
    tarifType: task ? (task.tarifType || '') : 'operateur',
    descRequired: task?.descRequired || false,
    validFrom: task?.validFrom || '',
    validTo: task?.validTo || '',
    replacedById: task?.replacedById || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const determinePreset = () => {
    if (formData.billable === false) return 'non-billable';
    if (formData.billingMethod === 'forfait') return 'forfait';
    if (formData.billingMethod === 'regie' && formData.tarifType === 'operateur') return 'regie-operateur';
    if (formData.billingMethod === 'regie' && formData.tarifType === 'special') return 'regie-special';
    if (sansMethodeAuDepart && !formData.billingMethod) return 'sans-methode';
    return '';
  };
  // Remplaçante : seulement une tâche ACTIVE du projet, valable APRÈS la fin de celle-ci (pas de choix
  // contradictoire). La remplaçante déjà enregistrée reste affichée.
  const remplacantesPossibles = autresTaches.filter(x => x.id === task?.replacedById
    || (x.isActive && (!formData.validTo || !x.validTo || x.validTo > formData.validTo)));

  const setPreset = (preset) => {
    setError('');
    if (preset === 'regie-operateur') setFormData(f => ({ ...f, billable: true, billingMethod: 'regie', tarifType: 'operateur' }));
    else if (preset === 'regie-special') setFormData(f => ({ ...f, billable: true, billingMethod: 'regie', tarifType: 'special' }));
    else if (preset === 'forfait') setFormData(f => ({ ...f, billable: true, billingMethod: 'forfait', tarifType: '' }));
    else if (preset === 'non-billable') setFormData(f => ({ ...f, billable: false, billingMethod: '', tarifType: '' }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.name.trim()) { setError('Le nom est requis'); return; }
    const preset = determinePreset();
    if (!preset) { setError('Choisissez un type de facturation'); return; }
    if (formData.validFrom && formData.validTo && formData.validFrom > formData.validTo) {
      setError('La date « valable à partir du » doit précéder la date « valable jusqu\'au ».');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } catch (err) {
      if (err.status === 409) {
        onConflict();
      } else {
        setError(err.message || 'Erreur inattendue');
        setSaving(false);
      }
    }
  };

  const currentPreset = determinePreset();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>{task ? 'Modifier la tache' : 'Nouvelle tache'}</h2>

        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label>Nom <span style={{color:'red'}}>*</span></label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
            placeholder="Ex: Regie SETU operateur"
            autoFocus
          />
        </div>

        <div className="form-group">
          <label>Type de facturation <span style={{color:'red'}}>*</span></label>
          <div className="preset-grid">
            <button type="button"
              className={`preset-btn ${currentPreset === 'regie-operateur' ? 'active' : ''}`}
              onClick={() => setPreset('regie-operateur')}>
              <strong>Regie + Operateur</strong>
              <small>Facture par heure, tarif operateur</small>
            </button>
            <button type="button"
              className={`preset-btn ${currentPreset === 'regie-special' ? 'active' : ''}`}
              onClick={() => setPreset('regie-special')}>
              <strong>Regie + Special</strong>
              <small>Facture par heure, tarif special</small>
            </button>
            <button type="button"
              className={`preset-btn ${currentPreset === 'forfait' ? 'active' : ''}`}
              onClick={() => setPreset('forfait')}>
              <strong>Forfait</strong>
              <small>Prix fixe, peu importe les heures</small>
            </button>
            <button type="button"
              className={`preset-btn ${currentPreset === 'non-billable' ? 'active' : ''}`}
              onClick={() => setPreset('non-billable')}>
              <strong>Non facturable</strong>
              <small>Taches internes, formations, etc.</small>
            </button>
            {sansMethodeAuDepart && (
              <button type="button"
                className={`preset-btn ${currentPreset === 'sans-methode' ? 'active' : ''}`}
                onClick={() => { setError(''); setFormData(f => ({ ...f, billable: true, billingMethod: '', tarifType: '' })); }}>
                <strong>Facturable, méthode non précisée</strong>
                <small>Comme aujourd&apos;hui (inchangé)</small>
              </button>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Validité (date de la prestation)</label>
          <div style={{display:'flex', gap:'0.75rem', flexWrap:'wrap', alignItems:'center'}}>
            <span>à partir du</span>
            <input type="date" value={formData.validFrom}
              max={formData.validTo || undefined}
              onChange={(e) => setFormData(f => ({ ...f, validFrom: e.target.value }))} />
            <span>jusqu&apos;au</span>
            <input type="date" value={formData.validTo}
              min={formData.validFrom || undefined}
              onChange={(e) => setFormData(f => ({ ...f, validTo: e.target.value, replacedById: e.target.value ? f.replacedById : '' }))} />
          </div>
          <small style={{color:'#64748b'}}>Vide = sans limite. Hors de cette période, la tâche n&apos;est pas proposée et Zeus la refuse.</small>
        </div>

        {task && (
          <div className="form-group">
            <label>Remplacée par (après la fin de validité)</label>
            <select value={formData.validTo ? formData.replacedById : ''} disabled={!formData.validTo}
              onChange={(e) => setFormData(f => ({ ...f, replacedById: e.target.value }))}>
              <option value="">{formData.validTo ? '— Aucune —' : '— Indiquez d\'abord « jusqu\'au » —'}</option>
              {remplacantesPossibles.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <small style={{color:'#64748b'}}>Les congés, maladies et tournées automatiques passent sur la remplaçante.</small>
          </div>
        )}

        <div className="form-group">
          <label style={{cursor:'pointer'}}>
            <input
              type="checkbox"
              checked={formData.descRequired}
              onChange={(e) => setFormData(f => ({ ...f, descRequired: e.target.checked }))}
              style={{marginRight:'0.5rem'}}
            />
            Description obligatoire au pointage
          </label>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Annuler</button>
          <button type="button" className="btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Enregistrement...' : (task ? 'Enregistrer' : 'Creer')}
          </button>
        </div>
      </div>
    </div>
  );
}
function ExistingTasksModal({ projectId, existingTaskNames, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Map());
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    let active = true;
    tasksApi.listTemplates()
      .then(data => {
        if (!active) return;
        const available = (data || []).filter(t => !existingTaskNames.has(t.name));
        setTemplates(available);
        setLoading(false);
      })
      .catch(err => {
        if (!active) return;
        setLoadError(err.message || 'Erreur chargement');
        setLoading(false);
      });
    return () => { active = false; };
  }, [existingTaskNames]);

  const filtered = useMemo(() => {
    if (!search) return templates;
    const q = search.toLowerCase();
    return templates.filter(t => t.name.toLowerCase().includes(q));
  }, [templates, search]);

  const toggle = (template) => {
    const next = new Map(selected);
    if (next.has(template.name)) next.delete(template.name);
    else next.set(template.name, { ...template });
    setSelected(next);
  };

  const toggleAll = () => {
    const allChecked = filtered.length > 0 && filtered.every(t => selected.has(t.name));
    const next = new Map(selected);
    if (allChecked) {
      for (const t of filtered) next.delete(t.name);
    } else {
      for (const t of filtered) if (!next.has(t.name)) next.set(t.name, { ...t });
    }
    setSelected(next);
  };

  const determinePresetFor = (t) => {
    if (t.billable === false) return 'non-billable';
    if (t.billingMethod === 'forfait') return 'forfait';
    if (t.billingMethod === 'regie' && t.tarifType === 'operateur') return 'regie-operateur';
    if (t.billingMethod === 'regie' && t.tarifType === 'special') return 'regie-special';
    return '';
  };

  const setPreset = (name, preset) => {
    const next = new Map(selected);
    const cur = next.get(name);
    if (preset === 'regie-operateur') next.set(name, { ...cur, billable: true, billingMethod: 'regie', tarifType: 'operateur' });
    else if (preset === 'regie-special')  next.set(name, { ...cur, billable: true, billingMethod: 'regie', tarifType: 'special' });
    else if (preset === 'forfait')        next.set(name, { ...cur, billable: true, billingMethod: 'forfait', tarifType: null });
    else if (preset === 'non-billable')   next.set(name, { ...cur, billable: false, billingMethod: null, tarifType: null });
    setSelected(next);
  };

  const setDescRequired = (name, val) => {
    const next = new Map(selected);
    next.set(name, { ...next.get(name), descRequired: val });
    setSelected(next);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrors([]);
    setProgress({ done: 0, total: selected.size });
    const errs = [];
    let done = 0;
    for (const [name, taskData] of selected) {
      try {
        await tasksApi.create(projectId, {
          name,
          billable: taskData.billable,
          billingMethod: taskData.billable ? taskData.billingMethod : null,
          tarifType: (taskData.billable && taskData.billingMethod === 'regie') ? taskData.tarifType : null,
          descRequired: !!taskData.descRequired,
        });
      } catch (err) {
        errs.push({ name, error: err.message || 'Erreur' });
      }
      done++;
      setProgress({ done, total: selected.size });
    }
    setSubmitting(false);
    if (errs.length === 0) onSaved();
    else setErrors(errs);
  };

  const presetBtn = (active) => ({
    padding: '4px 10px',
    background: active ? '#6366f1' : '#fff',
    color:      active ? '#fff'    : '#475569',
    border:     '1px solid ' + (active ? '#4f46e5' : '#CBD5E1'),
    borderRadius: 4,
    fontSize: 12,
    cursor: 'pointer',
    fontWeight: active ? 500 : 400,
  });

  if (loading) {
    return (
      <div onClick={onClose} style={modalOverlayStyle}>
        <div onClick={e => e.stopPropagation()} style={modalContentStyle}>
          <p style={{ padding: 16 }}>Chargement des tâches existantes...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div onClick={onClose} style={modalOverlayStyle}>
        <div onClick={e => e.stopPropagation()} style={modalContentStyle}>
          <h2>Erreur</h2>
          <div className="error-message">{loadError}</div>
          <div className="modal-actions">
            <button className="btn-primary" onClick={onClose}>Fermer</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClose} style={modalOverlayStyle}>
      <div onClick={e => e.stopPropagation()} style={modalContentStyle}>
        {step === 1 ? (
          <>
            <h2>Ajouter des taches existantes</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
              Choisissez des taches deja existantes dans d'autres projets. Les noms deja presents sur ce projet sont exclus de la liste.
            </p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 14 }}
                autoFocus
              />
              <button onClick={toggleAll} className="btn-secondary" style={{ whiteSpace: 'nowrap' }}>
                {filtered.length > 0 && filtered.every(t => selected.has(t.name)) ? 'Tout decocher' : 'Tout cocher'}
              </button>
            </div>

            <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              {filtered.length === 0 ? (
                <p style={{ padding: 16, color: '#999', textAlign: 'center', fontSize: 13 }}>
                  {templates.length === 0
                    ? 'Toutes les taches modeles sont deja presentes sur ce projet.'
                    : 'Aucun resultat pour cette recherche.'}
                </p>
              ) : filtered.map(t => (
                <label key={t.name} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                  background: selected.has(t.name) ? '#EEF2FF' : 'transparent',
                }}>
                  <input
                    type="checkbox"
                    checked={selected.has(t.name)}
                    onChange={() => toggle(t)}
                  />
                  <span style={{ flex: 1, fontSize: 13 }}>{t.name}</span>
                  <span style={{ fontSize: 11, color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: 10 }}>
                    {t.projectCount} proj.
                  </span>
                </label>
              ))}
            </div>

            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button className="btn-secondary" onClick={onClose}>Annuler</button>
              <button
                className="btn-primary"
                onClick={() => setStep(2)}
                disabled={selected.size === 0}
              >
                Configurer ({selected.size}) →
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Configurer la facturation</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
              Les valeurs par defaut viennent du modele le plus utilise. Modifiez si necessaire.
            </p>

            <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              {Array.from(selected.entries()).map(([name, t]) => {
                const preset = determinePresetFor(t);
                return (
                  <div key={name} style={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontWeight: 500, marginBottom: 8, fontSize: 13 }}>{name}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                      <button type="button" onClick={() => setPreset(name, 'regie-operateur')} style={presetBtn(preset === 'regie-operateur')}>Regie + Op.</button>
                      <button type="button" onClick={() => setPreset(name, 'regie-special')}  style={presetBtn(preset === 'regie-special')}>Regie + Spe.</button>
                      <button type="button" onClick={() => setPreset(name, 'forfait')}        style={presetBtn(preset === 'forfait')}>Forfait</button>
                      <button type="button" onClick={() => setPreset(name, 'non-billable')}   style={presetBtn(preset === 'non-billable')}>Non fact.</button>
                    </div>
                    <label style={{ fontSize: 12, color: '#475569', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!t.descRequired}
                        onChange={(e) => setDescRequired(name, e.target.checked)}
                        style={{ marginRight: 6 }}
                      />
                      Description obligatoire au pointage
                    </label>
                  </div>
                );
              })}
            </div>

            {submitting && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                Ajout en cours : {progress.done}/{progress.total}...
              </div>
            )}

            {errors.length > 0 && !submitting && (
              <div style={{ background: '#FEE2E2', color: '#991B1B', padding: 8, marginTop: 8, borderRadius: 4, fontSize: 12 }}>
                <strong>{errors.length} erreur(s) sur {selected.size} :</strong>
                <ul style={{ margin: '4px 0 0 16px' }}>
                  {errors.map((e, i) => <li key={i}><strong>{e.name}</strong> : {e.error}</li>)}
                </ul>
              </div>
            )}

            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button className="btn-secondary" onClick={() => setStep(1)} disabled={submitting}>← Retour</button>
              <button className="btn-primary" onClick={handleSubmit} disabled={submitting || selected.size === 0}>
                {submitting
                  ? `Ajout... (${progress.done}/${progress.total})`
                  : `Ajouter ${selected.size} tache${selected.size > 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const modalOverlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};

const modalContentStyle = {
  background: '#fff',
  borderRadius: 8,
  padding: 24,
  maxWidth: 760,
  width: '90%',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};
